import type { Theme } from '@emotion/react';
import { css } from '@emotion/react';

import { breakpoint } from '@twistopayments/design-system/styles';

export const root = (theme: Theme) => css`
  border-radius: ${theme.borderRadius.sm};
  border: none;
  position: relative;

  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const variants = {
  default: {
    box: (theme: Theme) => css`
      padding: ${theme.spacing[8]};

      ${breakpoint.up('sm')} {
        padding: ${theme.spacing[16]};
      }
    `,
    content: ({
      startAdornment,
      endAdornment,
      theme,
    }: {
      startAdornment: boolean;
      endAdornment: boolean;
      theme: Theme;
    }) => [
      startAdornment &&
        css`
          margin-left: ${theme.spacing[8]};

          ${breakpoint.up('sm')} {
            margin-left: ${theme.spacing[16]};
          }
        `,
      endAdornment &&
        css`
          margin-right: ${theme.spacing[8]};

          ${breakpoint.up('sm')} {
            margin-right: ${theme.spacing[16]};
          }
        `,
    ],
  },
  squashed: {
    box: (theme: Theme) => css`
      display: inline-flex;
      padding: ${theme.spacing[8]};

      ${breakpoint.up('sm')} {
        padding: ${theme.spacing[8]};
      }
    `,
    content: ({
      startAdornment,
      endAdornment,
      theme,
    }: {
      startAdornment: boolean;
      endAdornment: boolean;
      theme: Theme;
    }) => [
      startAdornment &&
        css`
          margin-left: ${theme.spacing[8]};

          ${breakpoint.up('sm')} {
            margin-left: ${theme.spacing[8]};
          }
        `,
      endAdornment &&
        css`
          margin-right: ${theme.spacing[8]};

          ${breakpoint.up('sm')} {
            margin-right: ${theme.spacing[8]};
          }
        `,
    ],
  },
};

const backgroundColor = (color: keyof typeof colors, theme: Theme) =>
  ({
    primary: theme.palette.secondary.lowContrast,
    infoPrimary: theme.palette.success.mediumContrast,
    secondary: theme.palette.primary.lowContrast,
    success: theme.palette.success.lowContrast,
    warning: theme.palette.warning.lowContrast,
    error: theme.palette.error.lowContrast,
    grey: theme.palette.tertiary.lowContrast,
    transparent: 'none',
  })[color];

export const colors = {
  primary: (theme: Theme) => css`
    background: ${backgroundColor('primary', theme)};
    color: ${theme.palette.tertiary.upperMediumContrast};
  `,
  infoPrimary: (theme: Theme) => css`
    background: ${backgroundColor('infoPrimary', theme)};
    color: ${theme.palette.primary.mainContrast};
  `,
  secondary: (theme: Theme) => css`
    background: ${backgroundColor('secondary', theme)};
    color: ${theme.palette.tertiary.upperMediumContrast};
  `,
  success: (theme: Theme) => css`
    background: ${backgroundColor('success', theme)};
    color: ${theme.palette.tertiary.upperMediumContrast};
  `,
  warning: (theme: Theme) => css`
    background: ${backgroundColor('warning', theme)};
    color: ${theme.palette.tertiary.upperMediumContrast};
  `,
  error: (theme: Theme) => css`
    background: ${backgroundColor('error', theme)};
    color: ${theme.palette.error.main};
  `,
  grey: (theme: Theme) => css`
    background: ${backgroundColor('grey', theme)};
    color: ${theme.palette.tertiary.upperMediumContrast};
  `,
  transparent: (theme: Theme) => css`
    background: ${backgroundColor('transparent', theme)};
  `,
};

export const clickable = css`
  cursor: pointer;
`;

export const content = css`
  flex-grow: 1;
`;

const arrowElement = (theme: Theme) => css`
  position: absolute;
  content: '';
  display: block;
  left: 18px;

  width: ${theme.spacing[16]};
  height: ${theme.spacing[16]};
  clip-path: polygon(50% 50%, 0% 100%, 100% 100%);
`;

export const arrow = (color: keyof typeof colors, theme: Theme) => css`
  margin-top: ${theme.spacing[16]};
  overflow: visible;
  &::after {
    ${arrowElement(theme)};
    top: -${theme.spacing[16]};
    background: ${backgroundColor(color, theme)};
  }
`;
